import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import Select from 'react-select';
import {CivilHearing} from '../../../Data/CivilHearing';
import {LaborHearing} from '../../../Data/LaborHearing';
import {CriminalHearing} from '../../../Data/CriminalHearing';
import {AnotherHearing} from '../../../Data/AnotherHearing';
import {ModalityAudience} from '../../../Data/ModalityAudience';
import {ProfessionalTypeNecessary} from '../../../Data/ProfessionalTypeNecessary';
import {FormSectionTitle} from '../../styles';
import {DadosAudienciaProps, IDadosAudiencia} from '../types';
import {
  Container,
  ErrorMessage,
  FieldContainer,
  FormGrid,
  Label,
  selectComponents,
  selectTheme
} from './styles';
import {comum} from '../../../../styles/Themes/light';

const DadosAudiencia: React.FC<DadosAudienciaProps> = ({
  tituloFormulario,
  tipoAudienciaEspecifico,
  erros,
  setDadosAudiencia,
  dadosAudiencia
}) => {
  const [dados, setDados] = useState<IDadosAudiencia>(dadosAudiencia);
  const [errosLocais, setErrosLocais] = useState<any>({});
  const tipoAnterior = useRef<string>(tipoAudienciaEspecifico);

  const opcoesTipoAudiencia = useMemo(() => {
    switch (tipoAudienciaEspecifico) {
      case 'Audiência Cível':
        return CivilHearing;
      case 'Audiência Trabalhista':
        return LaborHearing;
      case 'Audiência Penal':
        return CriminalHearing;
      case 'Outras Audiências':
        return AnotherHearing;
      default:
        return CriminalHearing;
    }
  }, [tipoAudienciaEspecifico]);
  const opcoesModalidade = useMemo(() => ModalityAudience, []);
  const opcoesProfissionalNecessario = useMemo(() => ProfessionalTypeNecessary, []);

  // Sincroniza estado interno quando dadosAudiencia mudar (ex: duplicação)
  useEffect(() => {
    if (dadosAudiencia) {
      setDados(dadosAudiencia);
    }
  }, [dadosAudiencia]);

  useEffect(() => {
    if (tipoAnterior.current !== tipoAudienciaEspecifico) {
      setDados(prev => ({
        ...prev,
        tipo_audiencia: ''
      }));
      tipoAnterior.current = tipoAudienciaEspecifico;
    }
  }, [tipoAudienciaEspecifico]);

  useEffect(() => {
    if (Object.keys(erros).length === 0) {
      setErrosLocais({});
    }
  }, [erros]);

  // Atualiza os dados no componente pai - otimizado
  useEffect(() => {
    setDadosAudiencia(dados);
  }, [dados, setDadosAudiencia]);

  // Função para validar campo obrigatório
  const validarCampoObrigatorio = useCallback((valor: string, nomeCampo: string) => {
    if (!valor.trim()) {
      return `${nomeCampo} é obrigatório`;
    }
    return '';
  }, []);

  // Função para validar endereço virtual (obrigatório quando modalidade é online)
  const validarEnderecoVirtual = useCallback((endereco: string, modalidade: string) => {
    if (modalidade === 'online' || modalidade === 'Virtual') {
      if (!endereco.trim()) {
        return 'Endereço virtual é obrigatório para modalidade online';
      }
    }
    return '';
  }, []);

  // Função otimizada para lidar com mudanças nos campos
  const handleInputChange = useCallback((campo: keyof IDadosAudiencia, valor: any) => {
    setDados(prev => {
      const novosDados = {
        ...prev,
        [campo]: valor
      };

      // Validação em tempo real
      const novosErros = { ...errosLocais };
      
      // Validação dos campos obrigatórios
      if (campo === 'tipo_audiencia') {
        const erro = validarCampoObrigatorio(valor, 'Tipo de audiência');
        if (erro) {
          novosErros.tipo_audiencia = erro;
        } else {
          delete novosErros.tipo_audiencia;
        }
      }
      
      if (campo === 'modalidade') {
        const erro = validarCampoObrigatorio(valor, 'Modalidade');
        if (erro) {
          novosErros.modalidade = erro;
        } else {
          delete novosErros.modalidade;
        }
        
        // Se mudou para presencial, limpa erro do endereço virtual
        if (valor === 'presencial' || valor === 'Presencial') {
          delete novosErros.endereco_virtual;
        }
      }
      
      if (campo === 'profissional_necessario') {
        const erro = validarCampoObrigatorio(valor, 'Profissional necessário');
        if (erro) {
          novosErros.profissional_necessario = erro;
        } else {
          delete novosErros.profissional_necessario;
        }
      }
      
      if (campo === 'endereco_virtual') {
        const erro = validarEnderecoVirtual(valor, novosDados.modalidade);
        if (erro) {
          novosErros.endereco_virtual = erro;
        } else {
          delete novosErros.endereco_virtual;
        }
      }

      setErrosLocais(novosErros);
      return novosDados;
    });
  }, [errosLocais, validarCampoObrigatorio, validarEnderecoVirtual]);

  // Função para lidar com mudanças nos selects
  const handleSelectChange = useCallback((campo: keyof IDadosAudiencia) => {
    return (selected: any) => {
      handleInputChange(campo, selected?.value || '');
    };
  }, [handleInputChange]);

  // Função para lidar com mudança no endereço virtual
  const handleEnderecoVirtualChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    handleInputChange('endereco_virtual', e.target.value);
  }, [handleInputChange]);

  // Verifica se deve mostrar o campo de endereço virtual
  const shouldShowEnderecoVirtual = useMemo(() => {
    return dados.modalidade === 'online' || dados.modalidade === 'Virtual';
  }, [dados.modalidade]);

  // Função para criar estilos customizados com estado de erro
  const createSelectStyles = useCallback((hasError: boolean) => ({
    control: (provided: Record<string, unknown>, state: any) => ({
      ...provided,
      height: 48,
      boxShadow: "none",
      cursor: 'pointer',
      border: hasError 
        ? `1px solid ${comum.colors.primaryRed400}` 
        : '1px solid var(--ce2e8f0)',
      '&:hover': {
        border: hasError 
          ? `1px solid ${comum.colors.primaryRed400}` 
          : `1px solid ${comum.colors.interfaceTextGray}`,
      },
    }),
    placeholder: (provided: Record<string, unknown>) => ({
      ...provided,
      color: 'var(--c353a40)',
      fontSize: '16px',
      fontWeight: '400',
    }),
    singleValue: (provided: Record<string, unknown>) => ({
      ...provided,
      color: 'var(--c353a40)',
      fontSize: '16px',
    }),
    option: (provided: Record<string, unknown>) => ({
      ...provided,
      color: 'var(--c5a646e)',
      fontSize: '16px',
      fontWeight: '500',
    }),
    menu: (provided: Record<string, unknown>) => ({
      ...provided,
      zIndex: 3,
    }),
    menuPortal: (provided: Record<string, unknown>) => ({
      ...provided,
      zIndex: 3,
    }),
  }), []);

  return (
    <Container>
      <FormSectionTitle>{tituloFormulario}</FormSectionTitle>

      <FormGrid>
        {/* Tipo de Audiência */}
        <FieldContainer>
          <Label required>Tipo</Label>
          <Select
            name="tipo_audiencia"
            styles={createSelectStyles(!!(erros?.tipo_audiencia || errosLocais?.tipo_audiencia))}
            components={selectComponents}
            theme={selectTheme}
            placeholder="Selecionar"
            value={opcoesTipoAudiencia.find(option => option.value === dados.tipo_audiencia)}
            onChange={handleSelectChange('tipo_audiencia')}
            options={opcoesTipoAudiencia}
          />
          {(erros?.tipo_audiencia || errosLocais?.tipo_audiencia) && (
            <ErrorMessage>{erros?.tipo_audiencia || errosLocais?.tipo_audiencia}</ErrorMessage>
          )}
        </FieldContainer>

        {/* Modalidade */}
        <FieldContainer>
          <Label required>Modalidade</Label>
          <Select
            name="modalidade"
            styles={createSelectStyles(!!(erros?.modalidade || errosLocais?.modalidade))}
            components={selectComponents}
            theme={selectTheme}
            placeholder="Selecionar"
            value={opcoesModalidade.find(option => option.value === dados.modalidade)}
            onChange={handleSelectChange('modalidade')}
            options={opcoesModalidade}
          />
          {(erros?.modalidade || errosLocais?.modalidade) && (
            <ErrorMessage>{erros?.modalidade || errosLocais?.modalidade}</ErrorMessage>
          )}
        </FieldContainer>

        {/* Profissional Necessário */}
        <FieldContainer>
          <Label required>Profissional necessário</Label>
          <Select
            name="profissional_necessario"
            styles={createSelectStyles(!!(erros?.profissional_necessario || errosLocais?.profissional_necessario))}
            components={selectComponents}
            theme={selectTheme}
            placeholder="Selecionar"
            value={opcoesProfissionalNecessario.find(option => option.value === dados.profissional_necessario)}
            onChange={handleSelectChange('profissional_necessario')}
            options={opcoesProfissionalNecessario}
          />
          {(erros?.profissional_necessario || errosLocais?.profissional_necessario) && (
            <ErrorMessage>{erros?.profissional_necessario || errosLocais?.profissional_necessario}</ErrorMessage>
          )}
        </FieldContainer>

        {/* Endereço Virtual movido para linha de endereço no bloco de endereço (StepServico/BlocoEndereco) */}
      </FormGrid>
    </Container>
  );
};

export default DadosAudiencia;
export type {DadosAudienciaProps, IDadosAudiencia} from '../types';
